import { Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NavService {

  /**
   * Reference to MatSidenav
   */
  public appDrawer: any;

  /**
   * Current router URL
   */
  public currentUrl = new BehaviorSubject<string>(this.getInitialUrl());

  /**
   * Stores expanded menu state
   * Key = RoutingUrl
   */
  private expandedItems = new Set<number | string>();

  constructor(private router: Router) {

    this.router.events.subscribe((event: Event) => {

      if (event instanceof NavigationEnd) {

        this.currentUrl.next(event.urlAfterRedirects);

        /**
         * OPTIONAL:
         * Auto-expand parents based on current route.
         * Uncomment if needed.
         */
        // this.expandParentsFromUrl(event.urlAfterRedirects);
      }
    });
  }

  /**
   * Get initial router URL safely
   */
  private getInitialUrl(): string {

    try {
      return this.router.url || '/';
    }
    catch (error) {

      console.warn('Could not retrieve initial URL:', error);

      return '/';
    }
  }

  // =========================================================
  // SIDENAV CONTROLS
  // =========================================================

  public closeNav(): void {

    if (this.appDrawer) {
      this.appDrawer.close();
    }
  }

  public openNav(): void {

    if (this.appDrawer) {
      this.appDrawer.open();
    }
  }

  public toggleNav(): void {

    if (this.appDrawer) {
      this.appDrawer.toggle();
    }
  }

  // =========================================================
  // MENU EXPANSION STATE
  // =========================================================

  /**
   * Check if a menu item is expanded
   */
  public isExpanded(id: number | string): boolean {
    if (id === null || id === undefined) return false;
    return this.expandedItems.has(id);
  }

  /**
   * Expand a menu item
   */
  public expand(id: number | string): void {
    if (id !== null && id !== undefined) {
      this.expandedItems.add(id);
    }
  }

  /**
   * Collapse a menu item
   */
  public collapse(id: number | string): void {
    this.expandedItems.delete(id);
  }

  /**
   * Toggle expanded/collapsed state
   */
  public toggleExpanded(id: number | string): void {
    if (id === null || id === undefined) return;

    if (this.expandedItems.has(id)) {
      this.expandedItems.delete(id);
    } else {
      this.expandedItems.add(id);
    }
  }

  /**
   * Collapse all expanded menu items
   */
  public collapseAll(): void {
    this.expandedItems.clear();
  }

  /**
   * Get all expanded menu routes
   */
  public getExpandedItems(): (number | string)[] {
    return Array.from(this.expandedItems);
  }

  /**
   * Restore expanded state
   * Useful if persisting in localStorage/sessionStorage
   */
  public restoreExpandedState(ids: (number | string)[]): void {
    this.expandedItems.clear();
    ids?.forEach(id => this.expandedItems.add(id));
  }

  // =========================================================
  // OPTIONAL HELPERS
  // =========================================================

  /**
   * Expand menu hierarchy from current URL
   * Useful for deep-linked routes.
   *
   * Example:
   * /admin/users/edit
   * expands:
   * admin
   * admin/users
   */
  private expandParentsFromUrl(url: string): void {

    if (!url) {
      return;
    }

    const cleanUrl = url.replace(/^\//, '');

    const segments = cleanUrl.split('/');

    let currentPath = '';

    segments.forEach(segment => {

      currentPath += currentPath ? `/${segment}` : segment;

      this.expandedItems.add(currentPath);
    });
  }
}
